import { ArrowLeft } from 'lucide-react';
import { useNavigate, useParams } from 'react-router-dom';

import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { RecurringExpenseForm } from '../components/RecurringExpenseForm';
import {
  useCreateRecurringExpense,
  useRecurringExpense,
  useUpdateRecurringExpense,
} from '../queries';
import type { RecurringExpenseInput } from '../types';

export function RecurringExpenseFormPage() {
  const { publicId = '' } = useParams();
  const isEdit = Boolean(publicId);
  const navigate = useNavigate();

  const query = useRecurringExpense(isEdit ? publicId : '');
  const create = useCreateRecurringExpense();
  const update = useUpdateRecurringExpense(publicId);

  async function handleSubmit(values: RecurringExpenseInput) {
    if (isEdit) await update.mutateAsync(values);
    else await create.mutateAsync(values);
    navigate('/finance/recurring-expenses');
  }

  if (isEdit && query.isPending) {
    return (
      <div className="max-w-xl space-y-4">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  const rec = query.data;

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="icon" onClick={() => navigate('/finance/recurring-expenses')}>
          <ArrowLeft />
        </Button>
        <h1 className="text-2xl font-semibold tracking-tight">
          {isEdit ? 'Edit recurring expense' : 'New recurring expense'}
        </h1>
      </div>

      <RecurringExpenseForm
        submitLabel={isEdit ? 'Save changes' : 'Create'}
        defaultValues={
          rec
            ? {
                scope: rec.employee ? 'employee' : 'company',
                employee: rec.employee ? String(rec.employee) : '',
                description: rec.description,
                category: rec.category,
                vendor: rec.vendor,
                frequency: rec.frequency,
                amount: rec.amount,
                currency: rec.currency as 'USD' | 'EUR' | 'GBP' | 'AUD',
                start_date: rec.start_date,
                end_date: rec.end_date ?? '',
                is_active: rec.is_active,
                notes: rec.notes,
              }
            : undefined
        }
        onSubmit={handleSubmit}
        onCancel={() => navigate('/finance/recurring-expenses')}
      />
    </div>
  );
}
